import _ from 'lodash';
import styled from 'styled-components';
import {
  Flex,
  Separator,
  LegendSymbol
} from 'components/base/layout-components';
import { getColor } from 'common/constants/_colors';
import { getAreaChartColor } from '../services/projects.services';
import { DELIVERY_STATES_API_KEYS } from '../projects.consts';
import LegendItem from './LegendItem';
import { useProjectsLocalStorage } from '../hooks/useProjectsLocalStorage';
import { getDeliveryTextsByKey } from '../services/projectExtended.services';

export const LegendLine = styled.div`
  width: 8px;
  height: 6px;
  ${({ backgroundColor }) =>
    `border-top: 2px solid ${getColor(backgroundColor)}`};
`;

const LegendLabel = styled(Flex)`
  padding: 0.3rem 0.5rem 0 0.5rem;
  margin-top: -0.3rem;
  margin-left: -0.5rem;
`;

const LegendLinkLabel = styled(Flex)`
  cursor: pointer;
  padding: 0.3rem 0.5rem 0 0.5rem;
  margin-top: -0.3rem;
  margin-left: -0.5rem;
  border-radius: 4px;
  ${({ isSelected }) =>
    isSelected ? `background-color: ${getColor('athensGray')}` : ''};
  :hover {
    background-color: ${getColor('athensGray')};
  }
`;

const ExcludedIcon = styled.div`
  div:first-child[type='square'] {
    background-color: ${getColor('silverSand')};
  }
  div:first-child[type='line'] {
    border-color: ${getColor('silverSand')};
  }
`;

const DeliveryLegend = ({
  issuesData,
  projectName = '',
  projectId,
  projectsExcludedIssues = {},
  setProjectsExcludedIssues,
  selectedDeliveryState,
  setSelectedDeliveryState,
  isLegendHorizontal = true,
  isSingleIterationView = false,
  showTitleEllipsisAfter,
  isProjectsView,
  pdtContextForKanbanEnabled = false
}) => {
  const { storeProjectsExcludedIssues } = useProjectsLocalStorage();
  const isClickable =
    !isSingleIterationView ||
    (isSingleIterationView && pdtContextForKanbanEnabled);

  const legendIcon = (key, color) => {
    const legendIconColor = getAreaChartColor(key, color);

    return key === DELIVERY_STATES_API_KEYS.PLANNED ? (
      <LegendLine type='line' backgroundColor={legendIconColor} />
    ) : (
      <LegendSymbol type='square' backgroundColor={getColor(legendIconColor)} />
    );
  };

  const filterByIssueType = name => {
    const currentProjectExcludedIssues = _.get(
      projectsExcludedIssues,
      projectId,
      []
    );

    const newProjectsExcludedIssues = {
      [projectId]: currentProjectExcludedIssues.length
        ? _.xor(currentProjectExcludedIssues, [name])
        : [name]
    };

    const excludedIssues = Object.assign(
      projectsExcludedIssues || {},
      newProjectsExcludedIssues
    );

    storeProjectsExcludedIssues(
      projectName,
      name,
      { ...excludedIssues },
      setProjectsExcludedIssues
    );
  };

  const LegendLabelWithLink = ({ children, deliveryKey }) => {
    const isSelected =
      isSingleIterationView && selectedDeliveryState === deliveryKey;

    return (
      <LegendLinkLabel
        data-testid='legend-item-link'
        direction='column'
        isSelected={isSelected}
        onClick={() => {
          if (isSingleIterationView) {
            setSelectedDeliveryState(isSelected ? null : deliveryKey);
            document.querySelector('#iterations_scroll').scrollTop = 0;
          } else {
            filterByIssueType(deliveryKey);
          }
        }}
      >
        {children}
      </LegendLinkLabel>
    );
  };

  const IssuesLabelsHorizontal = ({
    deliveryKey,
    icon,
    tooltipTitle,
    name,
    sum,
    idx,
    issuesLength
  }) => {
    const isLast = issuesLength === parseInt(idx + 1);

    const legendItem = (
      <LegendItem
        icon={icon}
        sum={sum}
        tooltipTitle={tooltipTitle}
        name={name}
      />
    );

    return (
      <Flex data-testid='horizontal-legend-issue'>
        <Flex
          key={name}
          direction='column'
          {...(idx === 0 ? { ml: 'normal' } : {})}
        >
          {isClickable ? (
            <LegendLabelWithLink deliveryKey={deliveryKey}>
              {legendItem}
            </LegendLabelWithLink>
          ) : (
            <LegendLabel>{legendItem}</LegendLabel>
          )}
        </Flex>

        {!isLast && (
          <Separator
            color={getColor('catskillWhite')}
            height='unset'
            margin='0 1.4rem'
          />
        )}
      </Flex>
    );
  };

  const IssuesLabelsVertical = ({
    icon,
    sum,
    subValue,
    tooltipTitle,
    name,
    showTitleEllipsisAfter
  }) => {
    const legendItem = (
      <LegendItem
        icon={icon}
        sum={sum}
        subValue={subValue}
        tooltipTitle={tooltipTitle}
        name={name}
        isHorizontalView={false}
        showTitleEllipsisAfter={showTitleEllipsisAfter}
      />
    );

    return (
      <LegendLabel data-testid='vertical-legend-issue'>
        {legendItem}
      </LegendLabel>
    );
  };

  return (
    <Flex
      direction={isLegendHorizontal ? 'row' : 'column'}
      pl={isLegendHorizontal ? '' : 'normal'}
    >
      {issuesData.map(({ key, name, value, subValue, color }, idx) => {
        const isExcluded =
          !isSingleIterationView &&
          projectsExcludedIssues &&
          projectsExcludedIssues[projectId] &&
          projectsExcludedIssues[projectId].includes(key);

        const icon = isExcluded ? (
          <ExcludedIcon data-testid='excluded-icon'>
            {legendIcon(key, color)}
          </ExcludedIcon>
        ) : (
          legendIcon(key, color)
        );

        const { tooltipTitle, itemTitle } = getDeliveryTextsByKey(
          key,
          isProjectsView
        );

        return isLegendHorizontal ? (
          <IssuesLabelsHorizontal
            key={key}
            deliveryKey={key}
            icon={icon}
            tooltipTitle={tooltipTitle}
            name={itemTitle}
            sum={value}
            idx={idx}
            issuesLength={issuesData.length}
          />
        ) : (
          <IssuesLabelsVertical
            icon={icon}
            key={key}
            tooltipTitle={tooltipTitle}
            name={itemTitle}
            sum={value}
            subValue={subValue}
            showTitleEllipsisAfter={showTitleEllipsisAfter}
          />
        );
      })}
    </Flex>
  );
};

export default DeliveryLegend;
